import { IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { title } from 'process';

export interface FilePickerProps {
  title: string;
  options?: OpenFilePickerOptions & {
    multiple?: false | undefined;
  };
  needConfirmation?: boolean;
  confirmContent?: string | JSX.Element;
  onFileSelect?: (fileHandle: FileSystemFileHandle) => void;
  onFileConfirm?: (fileHandle: FileSystemFileHandle) => void;
  onFileUpload: (file: File) => Promise<void>;  
}

export function FilePicker({
  needConfirmation = false,
  ...props
}: FilePickerProps) {
  const { t } = useTranslation();
  const [unconfirmedFileHandle, setUnconfirmedFileHandle] =
    useState<FileSystemFileHandle>();

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

 const handleOpenFilePicker = async () => {
    try {
      const [fileHandle] = await window.showOpenFilePicker(props.options);
      props.onFileSelect?.(fileHandle);
      handleUploadFile(fileHandle, !needConfirmation);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return;
      } else {
        console.error(err.name, err);
      }
    }
  };

  function handleFileConfirmation() {
    if (unconfirmedFileHandle) {
      handleUploadFile(unconfirmedFileHandle, true);
      setUnconfirmedFileHandle(undefined);
    }
    setOpenConfirmDialog(false);
  }

  const handleUploadFile = async (
    fileHandle: FileSystemFileHandle,
    confirmed: boolean
  ) => {
    if (confirmed) {
      props.onFileConfirm?.(fileHandle);
      fileHandle.getFile().then(async (file) => {
        await props.onFileUpload(file);
      });
    } else {
      setOpenConfirmDialog(true);
      setUnconfirmedFileHandle(fileHandle);
    }
  };

  function onAbort() {
    setOpenConfirmDialog(false);
  }

  return (
    <>
      <ConfirmDialog
        title={props.title}
        open={openConfirmDialog}
        onAbort={onAbort}
        onClose={onAbort}
        onConfirm={handleFileConfirmation}
      >
        {props.confirmContent}
      </ConfirmDialog>
      <Tooltip title={props.title}>
        <IconButton
          onClick={handleOpenFilePicker}
          aria-label={t(props.title)}
        >
          <FileUploadIcon color="secondary" fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );
}
